import React, { createContext, useState, useContext, useEffect } from "react";

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || "";

// Admin Basic-auth lives in sessionStorage (set by the AdminDisputes view).
// Clear it whenever a normal user signs in so the two sessions can't tangle
// (e.g. the header showing ADMIN over a logged-in user).
const ADMIN_STORAGE_KEY = "disputefai_admin_auth";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (res.ok) {
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      sessionStorage.removeItem(ADMIN_STORAGE_KEY);
      return data;
    } else {
      throw new Error(data.error);
    }
  };

  const register = async (username, email, password, acceptedTerms = false) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, email, password, acceptedTerms }),
    });
    const data = await res.json();
    if (res.ok) {
      // No token: registration now sends a verification email instead of
      // logging in directly (see POST /api/auth/verify-email) — data is
      // just a { message } in that case, nothing to store yet.
      if (data.token) {
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        sessionStorage.removeItem(ADMIN_STORAGE_KEY);
      }
      return data;
    } else {
      throw new Error(data.error);
    }
  };

  const verifyEmail = async (verifyToken) => {
    const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: verifyToken }),
    });
    const data = await res.json();
    if (res.ok) {
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      sessionStorage.removeItem(ADMIN_STORAGE_KEY);
      return data;
    } else {
      throw new Error(data.error);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, verifyEmail, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
